import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ValidationErrors } from 'final-form';
import { Form } from 'react-final-form';

import { Page } from '@occc-app/design-system';
import { Button } from '@occc/ui-design-system';
import {
  CampaignFormProps,
  CampaignFormRenderProps,
  CampaignFormValues,
} from '@occc-app/routes/Campaign/types';
import { CreateCampaignFormRenderComponent } from '@occc-app/routes/Campaign/CampaignCreatePage/CreateCampaignFormRenderComponent';

import { RequiredFieldsType } from '../../../types';

type CreateCampaignPageProps = {
  campaignCreate: CampaignFormProps['onSubmit'];
  required?: RequiredFieldsType<Partial<CampaignFormValues>>;
  validate?: (values: Partial<CampaignFormValues>) => Promise<ValidationErrors>;
};

export const CreateCampaignPage = ({ validate, campaignCreate }: CreateCampaignPageProps) => {
  const navigate = useNavigate();

  const renderForm: CampaignFormProps['render'] = useCallback(
    ({ handleSubmit, values, valid, submitting }: CampaignFormRenderProps) => {
      const actions = [
        <Button key='cancel' variant='outlined' color='secondary' onClick={() => navigate(-1)}>
          Cancel
        </Button>,
        <Button
          key='save'
          variant='contained'
          color='secondary'
          onClick={handleSubmit}
          disabled={!valid || submitting}
          data-testid='campaign-create-button'
        >
          Save
        </Button>,
      ];

      return (
        <Page
          browserTabTitle='Create Campaign'
          pageHeaderProps={{ title: 'New Campaign', subtitle: 'Create a campaign to.....' }}
          actions={actions}
          containerProps={{ variant: 'md' }}
        >
          <CreateCampaignFormRenderComponent handleSubmit={handleSubmit} name={values.name} />
        </Page>
      );
    },
    [navigate],
  );

  return (
    <Form<CampaignFormValues> onSubmit={campaignCreate} render={renderForm} validate={validate} />
  );
};
